/**
 * ArticleLink — the ONLY sanctioned way to render an externally-sourced
 * (GDELT-indexed) article as a click-through in PaCC.
 *
 * Every external article URL is attacker-influenceable (a bad actor can plant
 * a page GDELT indexes). This component is the rendering half of the
 * media-link defence; `safe-link.ts` is the logic half. Rules enforced here:
 *
 *   1. The headline is only a LIVE link when `safeArticleHref(url)` returns a
 *      non-null http(s) href. If it returns null (poisoned scheme / malformed
 *      / missing url), the headline renders as PLAIN, NON-CLICKABLE text and
 *      the bare domain is shown — we never emit a live bad link, and we never
 *      silently drop the item.
 *   2. `target="_blank"` ALWAYS pairs with `rel="noopener noreferrer"`:
 *        - noopener  → the opened page cannot reach back via window.opener
 *                      (blocks reverse-tabnabbing).
 *        - noreferrer→ don't leak the PaCC URL as a Referer to the target.
 *   3. UNVERIFIED sources (T3 aggregator / unknown domain) get a visible
 *      "unverified source" chip + the bare domain. We do NOT hide them and we
 *      do NOT imply trust. T1/T2 render clean with a small tier label.
 *
 * What this component does NOT do (be honest with the operator):
 *   - It does NOT scan the destination for malware/phishing. A green tier
 *     label means "credible publisher", NOT "this link is safe to click".
 *     The recommended URL-reputation layer (Safe Browsing / URLhaus) runs at
 *     ingest time and is an open TODO — see safe-link.ts.
 */

import { safeArticleHref, classifyArticleTrust } from './safe-link';
import { CREDIBILITY_META, type CoverageArticle } from './types';

export interface ArticleLinkProps {
  article: Pick<CoverageArticle, 'headline' | 'source' | 'cred' | 'url'>;
  /** Optional override for the visible link/headline font size. */
  fontSize?: number;
}

/** Extract a bare display domain from a URL (or fall back to the raw source
 *  string). Pure + defensive — never throws. */
function displayDomain(article: Pick<CoverageArticle, 'source' | 'url'>): string {
  if (article.url) {
    try {
      return new URL(article.url).hostname;
    } catch {
      /* fall through to source */
    }
  }
  return article.source ?? '';
}

export function ArticleLink({ article, fontSize = 13 }: ArticleLinkProps) {
  const href = safeArticleHref(article.url);
  const { tier, trust } = classifyArticleTrust(article);
  const unverified = trust === 'unverified';
  const domain = displayDomain(article);
  const tierMeta = CREDIBILITY_META[tier];

  // The clickable headline (only when the href passed the scheme guard).
  const headline = href ? (
    <a
      href={href}
      target="_blank"
      // SECURITY: noopener noreferrer is mandatory on every external link.
      rel="noopener noreferrer"
      data-article-link="safe"
      style={{
        color: 'var(--bm-accent, #6ea3ff)',
        textDecoration: 'none',
        fontSize,
        lineHeight: 1.45,
        borderBottom: '1px solid transparent',
      }}
    >
      {article.headline}
      <span aria-hidden style={{ marginLeft: 4, opacity: 0.7 }}>↗</span>
    </a>
  ) : (
    // No safe href → render as plain text. We surface the raw domain so the
    // operator can still see (and manually vet) the origin, but we refuse to
    // make a poisoned/malformed URL clickable.
    <span
      data-article-link="blocked"
      title="Link withheld: the source URL is missing or uses a disallowed scheme (only http/https links are made clickable)."
      style={{ color: 'var(--bm-text)', fontSize, lineHeight: 1.45 }}
    >
      {article.headline}
    </span>
  );

  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8 }}>
      {headline}

      {/* Bare domain — always visible so the operator sees where the link
          goes (or would have gone) before clicking. */}
      <span
        style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
          color: 'var(--bm-text-subtle)',
        }}
      >
        {domain || '(no source)'}
      </span>

      {unverified ? (
        // UNVERIFIED — never hide, never imply trust. Aggregator / unknown
        // domain: the operator must weigh it themselves.
        <span
          data-trust="unverified"
          title="Unverified source: not on the curated official (T1) or mainstream (T2) allowlist. Treat with caution — this is a publisher-credibility flag, NOT a malware scan."
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, fontWeight: 700,
            letterSpacing: '.3px', textTransform: 'uppercase',
            color: 'var(--bm-cov-under, #d08a2c)',
            border: '1px solid var(--bm-cov-under, #d08a2c)55',
            borderRadius: 3, padding: '0 5px',
          }}
        >
          <span aria-hidden>⚠</span> unverified source
        </span>
      ) : (
        // T1/T2 — small, neutral tier label. NOT a safety claim.
        <span
          data-trust="trusted"
          title={`Tier ${tier} · ${tierMeta.word} (curated publisher — not a malware scan of the link)`}
          style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, fontWeight: 700,
            color: tierMeta.cssVar,
            border: `1px solid ${tierMeta.cssVar}55`,
            borderRadius: 3, padding: '0 5px',
          }}
        >
          {tierMeta.short} {tierMeta.word}
        </span>
      )}
    </span>
  );
}
